import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import type { CurrentUser } from '../lib/auth';
import { isAdminRole } from '../lib/constants';
import { findName, useOrg } from '../lib/org';
import { useMyEmployee } from '../lib/useMyEmployee';
import { ProfileBanner } from '../components/ProfileBanner';
import { useMe } from '../lib/useMe';
import { usePermissions } from '../lib/usePermissions';
import { api } from '../lib/api-client';
import type { EmployeeSummaryDto } from '../lib/types';
import {
  CompanyHolidaysCard,
  PendingApprovalsCard,
  TimeOffCard,
  WhosOutCard,
} from './home-cards';
import {
  EmploymentStatusPlaceholder,
  LocationPlaceholder,
  MyDirectReportsWidget,
  OnboardingAtPayforgePlaceholder,
} from './home/widget-placeholders';
import { TimesheetReminderBanners } from './home/TimesheetReminderBanners';

export function Home() {
  const user = useOutletContext<CurrentUser>();
  const me = useMe();

  // Effective-identity admin from the server-resolved permission set: false while impersonating,
  // so the impersonated view shows exactly the target's widgets.
  const { isAdmin, canView } = usePermissions();

  // Banner shows the EFFECTIVE identity: when impersonating, this is the
  // impersonated user's name/title; otherwise it equals the real user.
  const emp = useMyEmployee();
  const org = useOrg();
  const name = me.data?.effectiveDisplayName ?? user.displayName;
  const firstName = name.split(/\s+/)[0] ?? name;
  const jobTitle = emp.data ? findName(org?.jobTitles, emp.data.jobTitleId) : '';
  const roleLabel = isAdminRole(me.data?.effectiveRoleCode ?? user.roleCode)
    ? 'Admin'
    : 'Employee';

  // W8 is visible to admins and employees with direct reports (managers/approvers).
  // Shares the same cache key as MyDirectReportsWidget so no extra network call.
  const reports = useQuery({
    queryKey: ['home', 'my-direct-reports'],
    queryFn: () => api.get<EmployeeSummaryDto[]>('/v1/home/my-direct-reports'),
  });
  const hasDirectReports = (Array.isArray(reports.data) ? reports.data : []).length > 0;
  const showPendingApprovals = isAdmin || reports.isLoading || hasDirectReports;

  return (
    <section className="p-4 md:p-8" aria-label="Home">
      <ProfileBanner
        name={name}
        photoUrl={emp.data?.profilePictureUrl}
        greetingName={firstName}
        subtitle={jobTitle || roleLabel}
        greeting
      />
      <TimesheetReminderBanners />

      {/* Main grid. Cards equalize height PER ROW (default grid stretch), matching
          Figma where each row has its own height - not globally, which would inflate
          short widgets. 20px gaps per the design. Admin view pairs out as:
          Time Off | Who's Out, My Direct Reports | Company Holidays, Location |
          Employment Status. Onboarding pairs with Time Off Requests on the row below. */}
      <div className="mt-5 mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* W1 reads /v1/home/time-off, which the backend gates on PROFILE_TIMEOFF, so it
            hides entirely when the effective access level cannot view the Time Off tab. */}
        {canView('PROFILE_TIMEOFF') ? <TimeOffCard /> : null}
        <WhosOutCard />
        <MyDirectReportsWidget />
        <CompanyHolidaysCard />
        {isAdmin ? <LocationPlaceholder /> : null}
        {isAdmin ? <EmploymentStatusPlaceholder /> : null}
      </div>

      {/* W6 + W8 share one audience (admins and employees with direct reports), so they
          pair on their own final row - keeping Time Off Requests beside Onboarding
          instead of dropping to a row of its own below the grid. Onboarding's own null
          rule matches showPendingApprovals exactly, so this gate covers both. */}
      {showPendingApprovals ? (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <OnboardingAtPayforgePlaceholder isAdmin={isAdmin} />
          <PendingApprovalsCard />
        </div>
      ) : null}
    </section>
  );
}
